import { useEffect } from 'react'
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react'

type AnimatedNumberProps = {
  value: number
  /** Spring stiffness — higher = snappier */
  stiffness?: number
  /** Spring damping — higher = less bounce */
  damping?: number
}

/**
 * Animates smoothly from the previous value to the new value
 * whenever `value` changes, using a spring physics simulation.
 *
 * Usage:
 *   <AnimatedNumber value={plan.pricePerMonth} />
 *   $<AnimatedNumber value={42} /> / month
 */
export function AnimatedNumber({
  value,
  stiffness = 160,
  damping = 22,
}: AnimatedNumberProps) {
  const mv = useMotionValue(value)
  const spring = useSpring(mv, { stiffness, damping, restDelta: 0.5 })
  const rounded = useTransform(spring, Math.round)

  useEffect(() => {
    mv.set(value)
  }, [value, mv])

  return <motion.span>{rounded}</motion.span>
}
